import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ApiService } from '../../../_services/api.service';
import { Subject } from '../../../_interfaces/subject';
import { isToday, findMostRelevantEncounterIndex, scrollScheduleToIndex } from '../../../_helpers/utils';
import { MatRadioModule } from '@angular/material/radio';
import { AppointmentSkeletonComponent } from '../appointment-skeleton/appointment-skeleton.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DmyDateFieldComponent } from '../dmy-date-field/dmy-date-field.component';
import { LoadingVisibilityController } from '../../utils/loading-visibility';
import { calendarDayOffset, formatDmyDate } from '../../../_helpers/dmy-date';
import { AuthService } from '../../../_services/auth.service';
import { AnalyticsService } from '../../../_services/analytics.service';

/** The tab a patient was picked from: the scheduled list or the all-patients search. */
export type PatientSource = 'schedule' | 'search';

@Component({
  selector: 'app-patient-selector',
  standalone: true,
  imports: [CommonModule,
    FormsModule,
    MatTabsModule,
    MatIconModule,
    MatButtonModule,
    DmyDateFieldComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatMenuModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatRadioModule,
    AppointmentSkeletonComponent,
    ReactiveFormsModule, TranslateModule ],
  templateUrl: './patient-selector.component.html'
})
export class PatientSelectorComponent implements OnDestroy {
  @Output() patientSelected = new EventEmitter<Subject | null>();
  /**
   * Which list the selection came from, emitted immediately before [patientSelected]
   * so consumers of both see a consistent pair. Only schedule rows carry a
   * `_patient_list` id, so the visit step needs to know the difference.
   */
  @Output() patientSourceSelected = new EventEmitter<PatientSource | null>();
  @Input() selectable: Boolean = false;
  /** Visit selection flow: radios on both tabs, card rows and a framed tab strip. */
  @Input() visitFlow: boolean = false;

  @ViewChild('scheduledPatientsList') scheduledPatientsList!: ElementRef<HTMLDivElement>;

  isLoading = false;
  showLoadingSkeleton = false;
  isLegalOrg = false;
  selectedTabIndex = 0;
  subjects: Subject[] = [];
  displaySearchIntructions = true;
  selectedDate: Date = new Date();
  selectedListCode: string = 'all';
  searchQuery: FormControl = new FormControl('');
  allSubjects = new BehaviorSubject<Subject[]>([]);
  filteredSubjects = new BehaviorSubject<Subject[]>([]);
  subjectSelectedRef = new BehaviorSubject<Subject | null>(null);
  private selectedSource: PatientSource | null = null;
  private subjectsRef: Subject[] | null = null;
  private groupsCache: { code: string; count: number }[] = [];
  /** Last tab index from onTabChange; used to detect return from All patients, not initial tab 0. */
  private previousTabIndex = 0;
  private readonly loadingVisibility = new LoadingVisibilityController(visible => {
    this.showLoadingSkeleton = visible;
  });

  constructor(private apiService: ApiService, private snackBar: MatSnackBar, private translate: TranslateService, private authService: AuthService, private analytics: AnalyticsService) {
    const user = this.authService.getUser();
    this.isLegalOrg = user?.entity?.settings?.category === 'Legal';
    this.loadSubjects();
    this.subjectSelectedRef.subscribe((patient: Subject | null) => {
      if (!patient) this.selectedSource = null;
      this.patientSourceSelected.emit(this.selectedSource);
      this.patientSelected.emit(patient);
    });
    this.searchQuery.valueChanges.subscribe(() => {
      this.filterSubjects();
    });
    this.filteredSubjects.subscribe(() => {
      this.checkSelectPatientListed();
    });
    this.allSubjects.subscribe(() => {
      this.checkSelectPatientListed();
    });
  }

  ngOnDestroy(): void {
    this.loadingVisibility.destroy();
  }

  async loadSubjects(): Promise<void> {
    this.isLoading = true;
    this.loadingVisibility.start();
    try {
      this.subjects = await this.apiService.getSubjects({ date: this.selectedDate, allSubjects: false });
      this.selectedListCode = 'all';
      this.subjectsRef = this.subjects;
      this.groupsCache = this.computeGroups(this.subjects);
    } catch {
      this.subjects = [];
      this.subjectsRef = this.subjects;
      this.groupsCache = [];
    } finally {
      this.filterSubjects();
      this.isLoading = false;
      this.loadingVisibility.stop();
      this.scrollToRelevantEncounter();
    }
  }

  get showRadios(): boolean {
    return this.visitFlow || Boolean(this.selectable);
  }

  get dateChipLabel(): string {
    return isToday(this.selectedDate) ? 'Today' : formatDmyDate(this.selectedDate);
  }

  get listChipLabel(): string {
    return this.selectedListCode === 'all' ? 'Patient list' : this.selectedListCode;
  }

  isSelected(item: Subject): boolean {
    return this.subjectSelectedRef.value?.id === item.id;
  }

  /** White card rows in the visit flow, tinted while selected. Returns one class so the two never collide. */
  rowTint(item: Subject): string {
    if (!this.visitFlow) return '';
    return this.isSelected(item) ? 'bg-surface-container-high' : 'bg-surface-bright';
  }

  get patientListGroups(): { code: string; count: number }[] {
    if (this.subjectsRef !== this.subjects) {
      this.subjectsRef = this.subjects;
      this.groupsCache = this.computeGroups(this.subjects);
    }
    return this.groupsCache;
  }

  filterSubjects(): void {
    const q = this.searchQuery.value.trim().toLowerCase();
    let items = this.subjects;
    if (this.selectedListCode && this.selectedListCode !== 'all') {
      items = items.filter(s => (s.patient_list_user || '').toLowerCase() === this.selectedListCode.toLowerCase());
    }
    if (!q) this.filteredSubjects.next(items);
    items = items.filter(item =>
      ([item.subject_prefix, item.subject_first_name, item.subject_middle_name, item.subject_last_name].filter(Boolean).join(' ').toLowerCase().includes(q)) ||
      (item.subject_id?.toLowerCase().includes(q)) ||
      (item.subject_alternative_id?.toLowerCase?.().includes(q) ?? false) ||
      (formatDmyDate(new Date(item.subject_dob)).includes(q))
    );
    this.filteredSubjects.next(items);
  }

  checkSelectPatientListed(): void {
    const allItems = [...this.filteredSubjects.value, ...this.allSubjects.value];
    const item = allItems.find((item: Subject) => item.id === this.subjectSelectedRef.value?.id) ;
    if (!item) this.subjectSelectedRef.next(null);
  }

  private computeGroups(list: Subject[]): { code: string; count: number }[] {
    const counts = new Map<string, number>();
    for (const s of list) {
      const code = (s.patient_list_user || '').trim();
      if (!code) continue;
      counts.set(code, (counts.get(code) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([code, count]) => ({ code, count }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }

  trackByGroupCode(_: number, g: { code: string; count: number }): string {
    return g.code;
  }

  formatTime(item: Subject): string {
    if (!item.admit_date) return '';
    const d = new Date(item.admit_date);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  formatName(item: Subject): string {
    const parts = [item.subject_prefix, item.subject_first_name, item.subject_middle_name, item.subject_last_name]
      .filter(Boolean);
    return parts.join(' ').replace(/\s+/g, ' ').trim();
  }

  formatDob(item: Subject): string {
    return formatDmyDate(new Date(item.subject_dob));
  }

  onScheduleDateChange(date: Date | null): void {
    if (date) {
      this.selectedDate = date;
      this.analytics.track('schedule_viewed',
        { day_offset: calendarDayOffset(this.selectedDate),
          trigger: 'day_navigation'})
      this.loadSubjects();
    }
  }

  onPatientListChange(listCode: string): void {
    this.selectedListCode = listCode;
    this.filterSubjects();
  }

  onTabChange(index: number): void {
    if (index === 0) {
      if (this.previousTabIndex === 1) {
        this.analytics.track('schedule_viewed',
          { day_offset: calendarDayOffset(this.selectedDate),
            trigger: 'back_from_all_patients'})
      }
      this.scrollToRelevantEncounter();
    }
    this.previousTabIndex = index;
  }

  /** The calendar owns its arrow keys inside the menu; only Escape is left to the menu so it can close. */
  onCalendarKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') event.stopPropagation();
  }

  /** Selection is derived from the model, so clicking the row or the radio inside it agree. */
  onRowClick(patient: Subject, source: PatientSource): void {
    this.onSelectPatient(patient, !this.isSelected(patient), source);
  }

  onSelectPatient(patient: Subject, checked: boolean, source: PatientSource = 'schedule'): void {
    if (typeof checked !== 'undefined' && !checked) {
      this.subjectSelectedRef.next(null);
      return;
    }
    if (source === 'schedule') {
      const listIndex = this.filteredSubjects.value.findIndex(item => item.id === patient.id);
      if (listIndex >= 0) {
        this.analytics.track('visit_selected', {
          visit_id: String(patient.id),
          day_offset: calendarDayOffset(this.selectedDate),
          list_position: listIndex + 1,
        });
      }
    }
    this.selectedSource = source;
    this.subjectSelectedRef.next(patient);
  }

  async onSearch(text: string): Promise<void> {
    text = text.trim();
    if (text.length === 0) {
      this.allSubjects.next([]);
      this.displaySearchIntructions = true;
      return;
    }
    if (text.length < 2) return;

    this.isLoading = true;
    this.loadingVisibility.start();
    try {
      const res = await this.apiService.getSubjects({ searchQuery: text, searchLimit: 5, allSubjects: true });
      this.allSubjects.next(res.rows ?? []);
      this.displaySearchIntructions = false;
    } catch {
      this.allSubjects.next([]);
      this.snackBar.open(this.translate.instant('Unable to load patients.'), this.translate.instant('Close'), {
        duration: 2000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      });
    } finally {
      this.filterSubjects();
      this.isLoading = false;
      this.loadingVisibility.stop();
    }
  }

  onSearchQueryCleared(): void {
    if (!this.searchQuery.value?.trim()) {
      this.scrollToTop();
    }
  }

  private scrollToTop(): void {
    const container = this.scheduledPatientsList?.nativeElement;
    if (container) {
      container.scrollTop = 0;
    }
  }

  private scrollToRelevantEncounter(): void {
    if (!isToday(this.selectedDate) || this.subjects.length === 0) return;

    const index = findMostRelevantEncounterIndex(this.subjects);
    scrollScheduleToIndex(this.scheduledPatientsList?.nativeElement, index);
  }

}